import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import { routes } from '../../constants/routes'

// import SiteGuideController from './site-guide-controller'
import SoundController from './sound-controller'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import anime from 'animejs'
import UniversalShareIcon from '../elements/universal-share-icon'
// NOTE: Importing all the FontAwesome Brands is overkill, just get what we need
import IconYouTube from '../../assets/svgs/social/icon-square-youtube.svg'
import IconInstagram from '../../assets/svgs/social/icon-instagram-white.svg'
import IconBlueSky from '../../assets/svgs/social/icon-bluesky-white.svg'

const MENU_SVG = {
    points: [[0, 0], [100, 0], [100, 25], [100, 50], [100, 75], [100, 100], [0, 100], [0, 75], [0, 50], [0, 25]]
}

const mapStateToProps = (state) => {
    const { ui, nav } = state
    return {
        uiWidth: ui.width,
        uiHeight: ui.height,
        navTheme: nav.navTheme
    }
}

const initPolygons = (points, width, height) => {
    const menuWidth = 350
    let allPoints = []
    points.forEach(point => {
        let xPos = point[0]
        let yPos = point[1]
        xPos = xPos / 100 * menuWidth + width - menuWidth
        yPos = yPos / 100 * height
        allPoints.push(`${xPos} ${yPos}`)
    })
    return allPoints.join(' ')
}

const calcOpenClosePos = (points, width, height) => {
    let allPoints = []
    const menuWidth = 350
    for (let i = 2; i >= 0; i = i - 1) {
        let pointSet = []
        points.forEach(point => {
            let xPos = point[0]
            let yPos = point[1]
            if (xPos === 0) {
                if (i > 0) {
                    // generate a random number between -50 and 50
                    let randX = Math.floor(Math.random() * 100) - 50
                    xPos = (width - menuWidth) / 3 * i + randX
                }
            } else if (xPos === 100) {
                xPos = width
            }
            if (yPos > 0 && yPos < 100) {
                let randY = Math.floor(Math.random() * 100) - 50
                yPos = yPos / 100 * height + randY
            } else if (yPos === 100) {
                yPos = height
            }
            pointSet.push(`${xPos} ${yPos}`)
        })
        allPoints.push({ value: pointSet.join(' ') })
    }
    return allPoints
}

const calcFinalPos = (points, width, height) => {
    let allPoints = []
    const menuWidth = 350
    for (let i = 2; i >= 0; i = i - 1) {
        let pointSet = []
        points.forEach(point => {
            let xPos = point[0]
            let yPos = point[1]
            if (xPos === 100) {
                if (i > 0) {
                    // generate a random number between -50 and 50
                    let randX = Math.floor(Math.random() * 100) - 50
                    xPos = (width - menuWidth) / 3 * i + randX
                }
            }
            if (yPos > 0 && yPos < 100) {
                let randY = Math.floor(Math.random() * 100) - 50
                yPos = yPos / 100 * height + randY
            } else if (yPos === 100) {
                yPos = height
            }
            pointSet.push(`${xPos} ${yPos}`)
        })
        allPoints.push({ value: pointSet.join(' ') })
    }
    return allPoints
}

const AppNav = ({ uiWidth, uiHeight, navTheme }) => {
    const [navOpen, setNavOpen] = useState(false)
    const [linkTitle, setLinkTitle] = useState('')
    const [polyPoints, setPolyPoints] = useState(initPolygons(MENU_SVG.points, uiWidth, uiHeight))
    const navigate = useNavigate()
    const navLinkRef = useRef(null)
    const isDev = process.env.NODE_ENV !== 'production'

    useEffect(() => {
        if (navOpen) {
            setPolyPoints(initPolygons(MENU_SVG.points, uiWidth, uiHeight))
            const leftPos = -1 * uiWidth + 350
            // update the left position of the nav-svg
            anime({ targets: '.nav-svg', duration: 10, left: `${leftPos}px` })
        }
    }, [navOpen, uiWidth, uiHeight])

    const handleLinkClick = (evt, path, linkLabel) => {
        evt.preventDefault()
        setLinkTitle(linkLabel)
        const closedPoints = calcOpenClosePos(MENU_SVG.points, uiWidth, uiHeight)
        if (navLinkRef.current) {
            navLinkRef.current.classList.add('animate-out')
        }
        const animation = anime.timeline({ duration: 450, easing: 'easeInOutQuad' })
            .add({ targets: '.svg-slider', points: closedPoints, easing: 'steps(9)' })
            .add({ targets: '.closer', opacity: 0, offset: 250 }, 0)
            .add({ targets: '.link-title', opacity: 1 }, 600)
        animation.finished.then(() => {
            navigate(path)
            setTimeout(() => {
                animateOut()
            }, 500)
        })
    }

    const animateOut = () => {
        const finalPoints = calcFinalPos(MENU_SVG.points, uiWidth, uiHeight)
        const animated = anime.timeline({ duration: 350, easing: 'easeInOutQuad' })
            .add({ targets: '.link-title', opacity: 0 })
            .add({ targets: '.svg-slider', points: finalPoints, easing: 'steps(9)' }, 0)
        animated.finished.then(() => {
            resetNav()
        })
    }

    const resetNav = () => {
        const resetPoint = initPolygons(MENU_SVG.points, uiWidth, uiHeight)
        const animation = anime.timeline({ duration: 0 })
            .add({ targets: '.svg-slider', points: [{ value: resetPoint } ] })
            .add({ targets: '.closer', opacity: 1 })
            .add({ targets: '.link-title', opacity: 0 })
            .add({ targets: '.nav-svg', left: '0px' })
            .add({ targets: '.slider', right: '-35rem' })
        animation.finished.then(() => {
            setPolyPoints(resetPoint)
            setNavOpen(false)
            if (navLinkRef.current) {
                navLinkRef.current.scrollTo(0, 0)
                navLinkRef.current.classList.remove('animate-out')
            }
        })
    }

    const handleOpenClose = () => {
        if (!navOpen) {
            // calc the polygons
            setPolyPoints(initPolygons(MENU_SVG.points, uiWidth, uiHeight))
            const leftPos = -1 * uiWidth + 350
            anime.timeline({ duration: 450, easing: 'easeInOutQuad' })
                .add({ targets: '.nav-svg', left: `${leftPos}px` })
                .add({ targets: '.slider', right: '0' }, '-=250')
        } else {
            anime.timeline({ duration: 450, easing: 'easeInOutQuad' })
                .add({ targets: '.nav-svg', left: '0px' })
                .add({ targets: '.slider', right: '-35rem' }, '-=600')
        }
        setNavOpen(!navOpen)
    }

    const renderCopyright = () => {
        const currentYear = new Date().getFullYear()
        return (
            <span className="copyright">&copy; {currentYear}. &nbsp;All rights reserved.</span>
        )
    }

    return (
        <>
            {/* Navigation */ }
            <div className="app-nav">
                <div className={`menu-icons ${navTheme}`}>
                    {/* <SiteGuideController /> */}
                    <UniversalShareIcon />
                    <SoundController />
                    <button onClick={handleOpenClose} className="icon-link" aria-label="Toggle Navigation">
                        <FontAwesomeIcon icon="bars" aria-hidden="true" />
                    </button>
                </div>
                <div className={`slider ${navOpen ? 'open' : 'closed'}`}>
                    <svg className={`nav-svg ${navOpen ? 'open' : ''}`} xmlns="http://www.w3.org/2000/svg">
                        <polygon className="svg-slider" points={polyPoints} />
                    </svg>
                    <button className="closer" onClick={handleOpenClose} aria-label="Close Navigation">
                        <FontAwesomeIcon icon="times" aria-hidden="true" />
                    </button>
                    <div ref={navLinkRef} className="nav-links">
                        <div className="nav-links-wrapper">
                            <Link to={routes.homeIndex.path} onClick={(evt) => handleLinkClick(evt, routes.homeIndex.path, routes.homeIndex.label)} className="nav-link">Home</Link>
                            <Link to={routes.murphysIndex.path} onClick={(evt) => handleLinkClick(evt, routes.murphysIndex.path, routes.murphysIndex.label)} className="nav-link">Tales from the East Side</Link>
                            <Link to={routes.clayvilleIndex.path} onClick={(evt) => handleLinkClick(evt, routes.clayvilleIndex.path, routes.clayvilleIndex.label)} className="nav-link">Discover Clayville</Link>
                            <Link to={routes.systemicRacismExplained.path}
                                onClick={(evt) => handleLinkClick(evt, routes.systemicRacismExplained.path, routes.systemicRacismExplained.label)} className="nav-link">Systemic Racism Explained</Link>
                            <Link to={routes.keyIssuesIndex.path} onClick={(evt) => handleLinkClick(evt, routes.keyIssuesIndex.path, routes.keyIssuesIndex.label)} className="nav-link">Explore Key Issues</Link>
                            <Link to={routes.spiritStaysFree.path} onClick={(evt) => handleLinkClick(evt, routes.spiritStaysFree.path, routes.spiritStaysFree.label)} className="nav-link">Spirit Stays Free</Link>
                            <Link to={routes.resourcesIndex.path} onClick={(evt) => handleLinkClick(evt, routes.resourcesIndex.path, routes.resourcesIndex.label)} className="nav-link">Resources</Link>
                            {/* <Link to={routes.siteGuidesIndex.path} onClick={(evt) => handleLinkClick(evt, routes.siteGuidesIndex.path, routes.siteGuidesIndex.label)} className="nav-link">Site Guides</Link> */}
                            <hr />
                            <div className="nav-secondary-links">
                                <Link to={routes.about.path}
                                    onClick={(evt) => handleLinkClick(evt, routes.about.path, routes.about.label)} className="nav-link">About</Link>
                                <Link to={routes.community.path}
                                    onClick={(evt) => handleLinkClick(evt, routes.community.path, routes.community.label)} className="nav-link">Stay in Touch</Link>
                                <Link to={routes.termsOfService.path}
                                    onClick={(evt) => handleLinkClick(evt, routes.termsOfService.path, routes.termsOfService.label)} className="nav-link">Terms of Service</Link>
                                { isDev &&
                                    <Link to={routes.styleGuideIndex.path} onClick={(evt) => handleLinkClick(evt, routes.styleGuideIndex.path, routes.styleGuideIndex.label)} className="nav-link">Style Guide</Link>
                                }
                            </div>
                            <hr />
                            <div className="nav-footer">
                                <div className="nav-footer-social">
                                    <a className="social-link-main nav-link-social" href="//www.youtube.com/@SpiritStaysFree" title="YouTube" target="_blank" rel="noopener noreferrer">
                                        <span className="visually-hidden">Follow us on YouTube</span>
                                        <img src={IconYouTube} alt="YouTube" />
                                    </a>
                                    <a className="social-link-main nav-link-social" href="//www.instagram.com/WayOuttaNoWay_" title="Instagram" target="_blank" rel="noopener noreferrer">
                                        <span className="visually-hidden">Follow us on Instagram</span>
                                        <img src={IconInstagram} alt="Instagram" />
                                    </a>
                                    <a className="social-link-main social-bluesky nav-link-social" href="//bsky.app/profile/wayouttanoway.bsky.social" title="BlueSky" target="_blank" rel="noopener noreferrer">
                                        <span className="visually-hidden">Follow us on BlueSky</span>
                                        <img src={IconBlueSky} alt="BlueSky" />
                                    </a>
                                </div>
                                {renderCopyright()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="link-title">
                <span className="loading">Loading...</span>
                <span className="link-label">{linkTitle}</span>
            </div>
        </>
    )
}

export default connect(mapStateToProps)(AppNav)
